/**
 * PC / 平板 / 手机 识别（PC↔H5 跳转专用）
 *
 * 原则：仅「真手机」走 H5；PC 与平板（含改 UA 的平板）一律 PC 站。
 * 不能单靠 UA：平板可伪装 iPhone / 桌面 Chrome，须结合视口尺寸与 Client Hints。
 *
 * 同步维护：public/device-detect.boot.js、src/utils/uaHarmony.js
 * 须与移动端 utils/device-detect.js 判定一致，避免双端互踢 / 漏跳。
 */

import { isHarmonyMobileUa, isHarmonyTabletUa } from './uaHarmony'

/** 手机 portrait 逻辑短边上限（iPhone Pro Max ≈430pt，留余量；iPad mini 短边 ≈744） */
export const PHONE_MAX_MIN_VIEWPORT = 520

/** 短边 ≥ 此值且有触控 → 按平板处理（即使 UA 写 Mobile/iPhone） */
export const TABLET_MIN_MIN_VIEWPORT = 600

/** 长边平板阈值；须配合短边守卫，避免旗舰手机（长边 CSS ≥900）误判 */
export const TABLET_MIN_MAX_VIEWPORT = 900

function hasTouch() {
  if (typeof window === 'undefined') return false
  return 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0
}

function sidePair(w, h) {
  if (typeof w !== 'number' || typeof h !== 'number' || w <= 0 || h <= 0) return null
  return { min: Math.min(w, h), max: Math.max(w, h) }
}

/**
 * 设备短边 / 长边（成对取，勿混 visualViewport）
 * 优先 screen（横屏地址栏不会压短边）；若 screen 与 layout 短边差过大（疑似物理像素）则用 layout。
 */
function getDeviceSidePair() {
  if (typeof window === 'undefined') return { min: 0, max: 0 }
  const layout = sidePair(window.innerWidth, window.innerHeight)
  const screen = sidePair(window.screen?.width, window.screen?.height)
  if (screen && layout) {
    if (screen.min > layout.min * 1.8) return layout
    return screen
  }
  return screen || layout || { min: 0, max: 0 }
}

export function getViewportMinSide() {
  return getDeviceSidePair().min
}

export function getViewportMaxSide() {
  return getDeviceSidePair().max
}

function getClientHintsMobile() {
  try {
    const mobile = navigator.userAgentData?.mobile
    if (typeof mobile === 'boolean') return mobile
  } catch (e) {}
  const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : ''
  if (isHarmonyMobileUa(ua)) return true
  if (isHarmonyTabletUa(ua)) return false
  return null
}

/** UA 明示平板（可被改掉，仅作辅助） */
export function isUaTabletHint(ua = navigator.userAgent || '') {
  if (/iPad/i.test(ua)) return true
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true
  if (/Tablet|PlayBook|Silk|KFAPWI|KFARWI|SM-T\d/i.test(ua)) return true
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true
  if (isHarmonyTabletUa(ua)) return true
  return false
}

/**
 * 平板或桌面：应使用 PC 站（含改 UA 的平板）
 * - 短边 ≥600 → 平板
 * - 长边 ≥900 且短边 ≥520 → 平板（拦伪装 UA；旗舰手机短边≈430 不会误伤）
 * - Client Hints / UA 仅兜底
 */
export function isTabletOrDesktopLike() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return true

  const ua = navigator.userAgent || ''
  const { min: minSide, max: maxSide } = getDeviceSidePair()
  const touch = hasTouch()
  const hintsMobile = getClientHintsMobile()

  if (isUaTabletHint(ua)) return true

  if (touch && minSide >= TABLET_MIN_MIN_VIEWPORT) return true
  // 长边够大 + 短边超过手机上限 → 平板；勿单独用长边（iPhone Pro Max 长边 ≥900）
  if (touch && maxSide >= TABLET_MIN_MAX_VIEWPORT && minSide >= PHONE_MAX_MIN_VIEWPORT) {
    return true
  }

  // Client Hints 非手机
  if (hintsMobile === false) return true

  // Client Hints 称 mobile，但视口是平板级 → 视为改 UA
  if (hintsMobile === true && minSide >= PHONE_MAX_MIN_VIEWPORT) return true

  // UA 像手机，视口却是平板级
  if (/iPhone|iPod|Android.*Mobile|Mobile|OpenHarmony|HarmonyOS/i.test(ua) && minSide >= PHONE_MAX_MIN_VIEWPORT) {
    return true
  }

  // 无触控宽屏 → 桌面
  if (!touch && minSide > PHONE_MAX_MIN_VIEWPORT) return true

  return false
}

/** 是否真手机（仅此类应跳转 H5） */
export function isPhoneBrowser() {
  if (typeof navigator === 'undefined') return false
  if (isTabletOrDesktopLike()) return false

  const minSide = getViewportMinSide()
  if (minSide > PHONE_MAX_MIN_VIEWPORT) return false

  const hintsMobile = getClientHintsMobile()
  if (hintsMobile === true) return true
  if (hintsMobile === false) return false

  const ua = navigator.userAgent || ''
  if (/iPhone|iPod/i.test(ua)) return true
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return true
  if (isHarmonyMobileUa(ua)) return true
  if (/webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true

  return false
}

/** PC / 平板使用 PC 站 */
export function shouldUsePcWebsite() {
  return !isPhoneBrowser()
}
